/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import * as React from "react";
import {
  RecordingSession,
  RecordingSessionTrack,
} from "types/RecordingSession";
import { Heading16 } from "components/shared/Heading";
import { DebugPanelStat } from "./DebugPanelStat";
import { useSelector } from "react-redux";

const Tracks: React.FC<{ recordingSessionTracks: RecordingSessionTrack[] }> = ({
  recordingSessionTracks,
}) => {
  return (
    <ul>
      {recordingSessionTracks.map((track) => (
        <li key={track.id}>
          <DebugPanelStat
            label="Participant"
            value={`${track.displayName} (${track.adobeId})`}
          />
          {track.url ? (
            <>
              Track audio ({track.size} Bytes):{" "}
              <a href={track.url}>download audio</a>
            </>
          ) : (
            "Track audio: NO ATTACHED TRACK AUDIO"
          )}
          <br />
          Chunks count: {track.chunksCount}
        </li>
      ))}
    </ul>
  );
};

const RecordingSessionRow: React.FC<{ recordingSession: RecordingSession }> = ({
  recordingSession,
}) => {
  return (
    <div>
      <Heading16>Recording Session</Heading16>
      <DebugPanelStat label={"Source"} value={recordingSession.source} />
      <DebugPanelStat
        label="Recording date"
        value={I18n.toTime(
          "time.formats.list",
          Date.parse(recordingSession.createdAt)
        )}
      />
      <DebugPanelStat label="Session ID" value={recordingSession.id} />
      <DebugPanelStat
        label="Processing state"
        value={recordingSession.processingState}
      />
      <DebugPanelStat
        label="All files uploaded"
        value={recordingSession.allFilesUploaded.toString()}
      />
      <DebugPanelStat
        label={`${recordingSession.recordingSessionTracks.length} Tracks`}
      />
      <div style={{ marginLeft: "20px" }}>
        <Tracks
          recordingSessionTracks={recordingSession.recordingSessionTracks}
        />
      </div>
      <hr />
    </div>
  );
};

export const RecordingSessionsDebugSection: React.FC = () => {
  const recordingSessions = useSelector((state) =>
    Object.values(state.recordingSessions)
  );

  const recordingSessionRows = recordingSessions.map((recordingSession) => (
    <RecordingSessionRow
      key={recordingSession.id}
      recordingSession={recordingSession}
    />
  ));

  return (
    <div>
      <hr />
      <Heading16>Recording Sessions</Heading16>
      {recordingSessions.length > 0 ? (
        recordingSessionRows
      ) : (
        <p>No recording sessions available.</p>
      )}
    </div>
  );
};
